import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, map, Observable, tap } from 'rxjs';
import { EmployeeStatus } from 'src/app/enums/employee-status.enum';
import { ReportType } from 'src/app/enums/report-type.enum';
import { ColorHelper } from 'src/app/helpers/color.helper';
import { QuestionCategoryResult } from 'src/app/models/question-category-result.model';
import { PersonLegend } from 'src/app/models/report/person-legend.model';
import { ReportBlock } from 'src/app/models/report/report-block.model';
import { ReportOptions } from 'src/app/models/report/report-options.model';
import { ReportService } from 'src/app/services';
import { TextGuideService } from 'src/app/services/text-guide.service';

@UntilDestroy()
@Component({
  selector: 'app-personality-test-details',
  templateUrl: './personality-test-details.component.html',
  styleUrls: ['./personality-test-details.component.scss']
})
export class PersonalityTestDetailsComponent implements OnInit {

  public colors: string[] = ['#433696', '#17a5e3', '##F09443', '#2B9443', '#D94646'];
  
  public legend: PersonLegend[] = [];
  public reportOptions: ReportOptions;

  public data$: Observable<ReportBlock[]>;

  public reportStructure$: Observable<ReportBlock[]>;
  public confidenceTitles$: Observable<any[]>;

  constructor(private reportService: ReportService, private route: ActivatedRoute, private guideService: TextGuideService, private translate: TranslateService) { }

  ngOnInit(): void {
    // Initialize translated structures
    this.reportStructure$ = this.translate.stream([
      'PERSONALITY_TEST_DETAILS.MOTIVATION_PROBLEM_SOLVING',
      'PERSONALITY_TEST_DETAILS.BEHAVIOR',
      'PERSONALITY_TEST_DETAILS.ADDITIONAL_FACTORS',
      'PERSONALITY_TEST_DETAILS.DETERMINATION'
    ]).pipe(map(translations => [
      {
        title: translations['PERSONALITY_TEST_DETAILS.MOTIVATION_PROBLEM_SOLVING'],
        factorOrder: ['MOT003', 'MOT006', 'MOT008', 'MOT007', 'MOT004', 'MOT005', '-', 'MOT001', '-', 'MOT009', 'MOT002'],
        rowResultBottom: true
      },
      {
       title: translations['PERSONALITY_TEST_DETAILS.BEHAVIOR'],
       factorOrder: ['VIS002', 'VIS007', 'VIS006', 'VIS005', 'VIS004', 'VIS003', '-', 'VIS001'],
       rowResultBottom: true
      },
      {
        title: translations['PERSONALITY_TEST_DETAILS.ADDITIONAL_FACTORS'],
        subtitle: translations['PERSONALITY_TEST_DETAILS.DETERMINATION'],
        factorOrder: ['COM', '-', 'CONF'],
        rowResultBottom: false
      }
    ]));

    this.confidenceTitles$ = this.translate.stream([
      'PERSONALITY_TEST_DETAILS.PRIMARY_COMMUNICATION_STYLE',
      'PERSONALITY_TEST_DETAILS.PRIMARY_COMMUNICATION_STYLE_DESC',
      'PERSONALITY_TEST_DETAILS.SECONDARY_COMMUNICATION_STYLE',
      'PERSONALITY_TEST_DETAILS.SECONDARY_COMMUNICATION_STYLE_DESC',
      'PERSONALITY_TEST_DETAILS.PRIMARY_CONFLICT_MANAGEMENT_STYLE',
      'PERSONALITY_TEST_DETAILS.PRIMARY_CONFLICT_MANAGEMENT_STYLE_DESC',
      'PERSONALITY_TEST_DETAILS.SECONDARY_CONFLICT_MANAGEMENT_STYLE',
      'PERSONALITY_TEST_DETAILS.SECONDARY_CONFLICT_MANAGEMENT_STYLE_DESC'
    ]).pipe(map(translations => [
      { title: translations['PERSONALITY_TEST_DETAILS.PRIMARY_COMMUNICATION_STYLE'], index: 0, code: 'COM', text: translations['PERSONALITY_TEST_DETAILS.PRIMARY_COMMUNICATION_STYLE_DESC'] },
      { title: translations['PERSONALITY_TEST_DETAILS.SECONDARY_COMMUNICATION_STYLE'], index: 1, code: 'COM', text: translations['PERSONALITY_TEST_DETAILS.SECONDARY_COMMUNICATION_STYLE_DESC'] },
      { title: translations['PERSONALITY_TEST_DETAILS.PRIMARY_CONFLICT_MANAGEMENT_STYLE'], index: 0, code: 'CONF', text: translations['PERSONALITY_TEST_DETAILS.PRIMARY_CONFLICT_MANAGEMENT_STYLE_DESC'] },
      { title: translations['PERSONALITY_TEST_DETAILS.SECONDARY_CONFLICT_MANAGEMENT_STYLE'], index: 1, code: 'CONF', text: translations['PERSONALITY_TEST_DETAILS.SECONDARY_CONFLICT_MANAGEMENT_STYLE_DESC'] }
    ]));

    this.route.params
    .pipe(untilDestroyed(this))
    .subscribe(params => {
      const reviewPeriodEmployeeId: number | null = parseInt(params['reviewPeriodEmployeeId']);
   
      const guide = this.guideService.getGuide('assessment:SZILUETT');

      this.translate.stream([guide.titleKey, guide.subtitleKey])
        .pipe(untilDestroyed(this))
        .subscribe(translations => {
          this.reportOptions = {
            title: translations[guide.titleKey],
            subtitle: translations[guide.subtitleKey],
            titleIndexText: guide.titleIndexText,
            logoLink: guide.logoLink,
            reportType: ReportType.SziluettDetails
          } as ReportOptions;
        });

      if (reviewPeriodEmployeeId)
      {
        
        this.data$ = combineLatest([
          this.reportStructure$,
          this.confidenceTitles$,
          this.reportService.getPersonalityTestReport([reviewPeriodEmployeeId])
        ]).pipe(
          map(([reportStructure, confidenceTitles, data]) => {

            const dataForEmployee = data.items[0];

            this.reportOptions.showCreatedByPerson = true;

            // legend
            dataForEmployee.color = ColorHelper.getChartLegendColor(this.colors, 0);

            let emp = dataForEmployee.employee;
            if (emp.employeeStatus === EmployeeStatus.Inactive)
            {
              emp.name = emp.name + ' [' + this.translate.instant('EMPLOYEE.INACTIVE').toLocaleLowerCase() + ']';
            }
            
            this.legend.push({ person: emp, colorOptions: dataForEmployee.color, reviewDate: dataForEmployee.reviewDate });

            const result: ReportBlock[] = [];

            // report structure
            reportStructure.forEach(block => {

              const resultItem = { ...block };

              resultItem.items = [];
              resultItem.factorOrder.forEach(factorCode => {

                if (factorCode != '-')
                {
                  const item = dataForEmployee.categoryResults.find(x => x.questionCategory.code == factorCode);

                  if (factorCode === 'COM' || factorCode === 'CONF') {
                    let index = 0;

                    if (item && item.bestChildren) {
                      item.bestChildren.forEach(best => {
                        const confTitle = confidenceTitles.find(x => x.code == factorCode && x.index == index);

                        const value = best.questionCategory.name;
                        best.questionCategory.name = confTitle.title;
                        best.resultText = confTitle.text + ': <b>' + value + '</b>';
                        resultItem.items.push(best);

                        index++;
                      });
                    }


                  }
                  else
                  {
                    resultItem.items.push(item);
                  }
                }
                else
                {
                  resultItem.items.push({questionCategory: null} as QuestionCategoryResult);
                }

              });

              result.push(resultItem);

            });

            return result;
          })
        );

      }


    });

  }

}
